"use client";

import { useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import {
  Checkbox,
  ErrorNotice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  HINT,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import MazeSvg from "@/components/tools/games-organizers/maze-svg";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import { downloadPng, downloadSvg } from "@/lib/tool-kit/download";
import {
  clampRings,
  clampSide,
  generateMaze,
  MAX_RINGS,
  MAX_SIDE,
  MAZE_LEVELS,
  MAZE_PRESETS,
  type MazeLevel,
  type MazeShape,
  type MazeStyle,
  MIN_RINGS,
  MIN_SIDE,
  mazeStats,
} from "@/lib/tool-kit/games-organizers/maze";
import { drawMaze } from "@/lib/tool-kit/games-organizers/maze-geometry";
import {
  DEFAULT_SEED,
  formatSeed,
  newSeed,
  parseSeed,
} from "@/lib/tool-kit/worksheets/random";

/** English strings. A translated content file overrides these through `ui`. */
export const MAZE_GENERATOR_STRINGS = {
  shapeLabel: "Shape",
  shapes: { rectangular: "Rectangular", circular: "Circular" },
  levelLabel: "Difficulty (size)",
  levels: {
    easy: "Easy",
    medium: "Medium",
    hard: "Hard",
    extreme: "Extreme",
    custom: "Custom size",
  },
  levelSize: {
    rectangular: "{cols} × {rows} cells",
    circular: "{rings} rings",
  },
  colsLabel: "Columns ({min}–{max})",
  rowsLabel: "Rows ({min}–{max})",
  ringsLabel: "Rings ({min}–{max})",
  styleLabel: "Maze style",
  styles: { winding: "Winding", branching: "Branching" },
  styleHints: {
    winding:
      "Recursive backtracker: long corridors and a long route, with relatively few dead ends.",
    branching:
      "Wilson's algorithm: every possible maze equally likely, with many short dead ends to wander into.",
  },
  seedLabel: "Maze number",
  seedHint: "Type a number from 1 to 999999 to rebuild the same maze.",
  seedInvalid: "Use a whole number from 1 to 999999.",
  newMaze: "New maze",
  fromSeed: "Make maze from this number",
  showSolution: "Show solution",
  hideSolution: "Hide solution",
  printMaze: "Print maze",
  printWithKey: "Print maze and solution",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  nameLine: "Name and date line",
  titleLabel: "Title for the printed page",
  titlePlaceholder: "Maze",
  defaultTitle: "Maze",
  solutionTitle: "{title} — Solution",
  mazeNumber: "Maze {seed}",
  heading: "Your maze",
  legendRectangular:
    "The filled dot is the start, in the top-left corner. The ringed dot is the finish, in the bottom-right.",
  legendCircular:
    "The filled dot is the start, on the outer edge. The ringed dot is the finish, in the centre.",
  statsHeading: "About this maze",
  cells: "Cells",
  routeLength: "Cells on the solution",
  deadEnds: "Dead ends",
  decisionPoints: "Turns to choose on the route",
  name: "Name",
  date: "Date",
  mazeLabel: "Maze",
  solutionLabel: "Maze with the solution drawn",
  downloadFailed:
    "The PNG could not be created in this browser. Try Download SVG.",
} as const;

type Strings = typeof MAZE_GENERATOR_STRINGS;
type Level = MazeLevel | "custom";
type PrintTarget = "puzzle" | "key";

const SHEET_H2 = "font-semibold text-xl";
const FILE_PREFIX = "maze";

function sizeText(
  strings: Strings,
  shape: MazeShape,
  level: MazeLevel
): string {
  const preset = MAZE_PRESETS[level];
  return shape === "circular"
    ? t(strings.levelSize.circular, { rings: preset.rings })
    : t(strings.levelSize.rectangular, {
        cols: preset.cols,
        rows: preset.rows,
      });
}

function NameLine({ strings }: { strings: Strings }) {
  return (
    <div className="flex flex-wrap gap-x-10 gap-y-3 text-sm">
      <span className="flex items-end gap-2">
        {strings.name}
        <span className="inline-block w-56 border-current border-b" />
      </span>
      <span className="flex items-end gap-2">
        {strings.date}
        <span className="inline-block w-32 border-current border-b" />
      </span>
    </div>
  );
}

export default function MazeGeneratorWidget() {
  const strings = useToolUi(MAZE_GENERATOR_STRINGS);
  const fmt = useToolNumber();
  const svgRef = useRef<SVGSVGElement>(null);
  const [shape, setShape] = useState<MazeShape>("rectangular");
  const [level, setLevel] = useState<Level>("medium");
  const [style, setStyle] = useState<MazeStyle>("winding");
  const [customCols, setCustomCols] = useState("20");
  const [customRows, setCustomRows] = useState("15");
  const [customRings, setCustomRings] = useState("10");
  const [seed, setSeed] = useState(DEFAULT_SEED);
  const [seedText, setSeedText] = useState(String(DEFAULT_SEED));
  const [seedError, setSeedError] = useState(false);
  const [showRoute, setShowRoute] = useState(false);
  const [nameLine, setNameLine] = useState(true);
  const [title, setTitle] = useState("");
  const [printTarget, setPrintTarget] = useState<PrintTarget>("puzzle");
  const [downloadError, setDownloadError] = useState(false);

  const preset = MAZE_PRESETS[level === "custom" ? "medium" : level];
  const cols =
    level === "custom"
      ? clampSide(parseIntegerInput(customCols) ?? preset.cols)
      : preset.cols;
  const rows =
    level === "custom"
      ? clampSide(parseIntegerInput(customRows) ?? preset.rows)
      : preset.rows;
  const rings =
    level === "custom"
      ? clampRings(parseIntegerInput(customRings) ?? preset.rings)
      : preset.rings;

  const maze = useMemo(
    () => generateMaze({ shape, style, cols, rows, rings, seed }),
    [shape, style, cols, rows, rings, seed]
  );
  const drawing = useMemo(() => drawMaze(maze), [maze]);
  const stats = useMemo(() => mazeStats(maze), [maze]);

  function choose(next: number) {
    setSeed(next);
    setSeedText(String(next));
    setSeedError(false);
  }

  function fromSeed() {
    const parsed = parseSeed(seedText);
    if (parsed === null) {
      setSeedError(true);
      return;
    }
    choose(parsed);
  }

  function print(target: PrintTarget) {
    flushSync(() => setPrintTarget(target));
    window.print();
  }

  async function savePng() {
    setDownloadError(false);
    try {
      if (svgRef.current) {
        await downloadPng(
          `${FILE_PREFIX}-${formatSeed(seed)}.png`,
          svgRef.current,
          { scale: 3 }
        );
      }
    } catch {
      setDownloadError(true);
    }
  }

  const heading = title.trim() || strings.defaultTitle;
  const legend =
    shape === "circular" ? strings.legendCircular : strings.legendRectangular;
  const sheet = "space-y-4 text-foreground print:text-black";
  const levelOptions = [
    ...MAZE_LEVELS.map((value) => ({
      value,
      label: `${strings.levels[value]} — ${sizeText(strings, shape, value)}`,
    })),
    { value: "custom" as const, label: strings.levels.custom },
  ];

  return (
    <div className="space-y-6">
      <div className="space-y-4 print:hidden">
        <PillGroup
          label={strings.shapeLabel}
          name="maze-shape"
          onChange={setShape}
          options={(["rectangular", "circular"] as const).map((value) => ({
            value,
            label: strings.shapes[value],
          }))}
          value={shape}
        />
        <SelectField
          id="maze-level"
          label={strings.levelLabel}
          onChange={setLevel}
          options={levelOptions}
          value={level}
        />
        {level === "custom" && shape === "rectangular" ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id="maze-cols"
              label={t(strings.colsLabel, { min: MIN_SIDE, max: MAX_SIDE })}
              numeric
              onChange={setCustomCols}
              value={customCols}
            />
            <TextField
              id="maze-rows"
              label={t(strings.rowsLabel, { min: MIN_SIDE, max: MAX_SIDE })}
              numeric
              onChange={setCustomRows}
              value={customRows}
            />
          </div>
        ) : null}
        {level === "custom" && shape === "circular" ? (
          <TextField
            id="maze-rings"
            label={t(strings.ringsLabel, { min: MIN_RINGS, max: MAX_RINGS })}
            numeric
            onChange={setCustomRings}
            value={customRings}
          />
        ) : null}
        <PillGroup
          label={strings.styleLabel}
          name="maze-style"
          onChange={setStyle}
          options={(["winding", "branching"] as const).map((value) => ({
            value,
            label: strings.styles[value],
          }))}
          value={style}
        />
        <p className={HINT}>{strings.styleHints[style]}</p>
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_PRIMARY}
            onClick={() => choose(newSeed())}
            type="button"
          >
            {strings.newMaze}
          </button>
          <button
            aria-pressed={showRoute}
            className={BUTTON_SECONDARY}
            onClick={() => setShowRoute((on) => !on)}
            type="button"
          >
            {showRoute ? strings.hideSolution : strings.showSolution}
          </button>
        </div>
        <div className="grid items-end gap-3 sm:grid-cols-[1fr_auto]">
          <TextField
            hint={strings.seedHint}
            id="maze-seed"
            label={strings.seedLabel}
            mono
            numeric
            onChange={setSeedText}
            value={seedText}
          />
          <button className={BUTTON_SECONDARY} onClick={fromSeed} type="button">
            {strings.fromSeed}
          </button>
        </div>
        {seedError ? <ErrorNotice>{strings.seedInvalid}</ErrorNotice> : null}
        <TextField
          id="maze-title"
          label={strings.titleLabel}
          onChange={setTitle}
          placeholder={strings.titlePlaceholder}
          value={title}
        />
        <Checkbox
          checked={nameLine}
          label={strings.nameLine}
          onChange={setNameLine}
        />
      </div>

      <div className="space-y-3 print:hidden">
        <h2 className={WIDGET_H2}>{strings.heading}</h2>
        <p className={HINT}>
          {t(strings.mazeNumber, { seed: formatSeed(seed) })}
        </p>
        <MazeSvg
          drawing={drawing}
          label={showRoute ? strings.solutionLabel : strings.mazeLabel}
          showRoute={showRoute}
          svgRef={svgRef}
        />
        <p className={`${HINT} text-center`}>{legend}</p>
        <div className="flex flex-wrap justify-center gap-3">
          <button
            className={BUTTON_SECONDARY}
            onClick={() => print("puzzle")}
            type="button"
          >
            {strings.printMaze}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => print("key")}
            type="button"
          >
            {strings.printWithKey}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() =>
              svgRef.current &&
              downloadSvg(
                `${FILE_PREFIX}-${formatSeed(seed)}.svg`,
                svgRef.current
              )
            }
            type="button"
          >
            {strings.downloadSvg}
          </button>
          <button className={BUTTON_SECONDARY} onClick={savePng} type="button">
            {strings.downloadPng}
          </button>
        </div>
        {downloadError ? (
          <ErrorNotice>{strings.downloadFailed}</ErrorNotice>
        ) : null}
        <h3 className="font-semibold text-foreground text-lg">
          {strings.statsHeading}
        </h3>
        <StatGrid>
          <StatCard label={strings.cells} value={fmt(stats.cells)} />
          <StatCard
            label={strings.routeLength}
            value={fmt(stats.solutionLength)}
          />
          <StatCard label={strings.deadEnds} value={fmt(stats.deadEnds)} />
          <StatCard
            label={strings.decisionPoints}
            value={fmt(stats.decisionPoints)}
          />
        </StatGrid>
      </div>

      <section className={`hidden print:block ${sheet}`}>
        <h2 className={SHEET_H2}>{heading}</h2>
        {nameLine ? <NameLine strings={strings} /> : null}
        <MazeSvg
          drawing={drawing}
          label={strings.mazeLabel}
          showRoute={false}
        />
        <p className="text-center text-sm">{legend}</p>
      </section>
      {printTarget === "key" ? (
        <section className={`hidden break-before-page print:block ${sheet}`}>
          <h2 className={SHEET_H2}>
            {t(strings.solutionTitle, { title: heading })}
          </h2>
          <MazeSvg drawing={drawing} label={strings.solutionLabel} showRoute />
        </section>
      ) : null}
    </div>
  );
}
